import { FC, useRef, useState } from 'react';
import { TieredMenu } from 'primereact/tieredmenu';
import { Button } from 'primereact/button';
import { Avatar } from 'primereact/avatar';
import { MenuItem, MenuItemOptions } from 'primereact/menuitem';
import { useAuth0 } from '@auth0/auth0-react';
import User, { Roles } from '../../models/User';
import { Site } from '../../models/Site';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { resetUser, selectUser, setUser } from '../../redux/reducers/userSlice';
import { useUserActionMutation } from '../../custom-hooks/useUserActionMutation';
import { usePostHog } from '@posthog/react';
import { ChevronDown, ChevronUp, ExternalLink, LogOut } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { SPEECH_STREAM_START_POINT_ID, useSpeechStreamToolbar } from '../../speechstream/useSpeechStreamToolbar';
import { useSideNavActive } from '../LayoutWrapper/SideNavLayoutContext';

const HeaderProfileMenu: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { logout } = useAuth0();
  const dispatch = useAppDispatch();
  const profileMenu = useRef<TieredMenu>(null);
  const userRedux = useAppSelector(selectUser);
  const [commitLoginMutation] = useUserActionMutation();
  const posthog = usePostHog();
  const profileAvatarImage = userRedux?.picture;
  const [toggleButton, setToggleButton] = useState<boolean>(false);
  const { loadToolbar, loading: loadingToolbar } = useSpeechStreamToolbar(SPEECH_STREAM_START_POINT_ID);
  const sideNavActive = useSideNavActive();

  const adminRoles = [Roles.SITE_ADMIN, Roles.NETWORK_ADMIN];
  const rolesArray = [Roles.TEACHER, ...adminRoles];
  const isTeacherOrSiteAdminOrNetworkAdmin =
    userRedux?.roles?.some(role => rolesArray.includes(role?.name as Roles)) ?? false;

  const timeZonesList = Array.from(
    new Set(
      (userRedux?.sites ?? [])
        .map((site: Site) => site.timeZone?.name)
        .filter((timeZoneName): timeZoneName is string => Boolean(timeZoneName)),
    ),
  );

  const openExternalUrl = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Path the user is on when opening a demo, so the guided-tour banner can return here.
  const currentPath = () => `${location.pathname}${location.search}`;

  const externalLinkTemplate = (item: MenuItem, options: MenuItemOptions) => (
    <a
      className={`${options.className} tw-flex tw-flex-row tw-items-center tw-gap-x-2 tw-w-full`}
      onClick={options.onClick}
      role="menuitem"
    >
      {item.icon}
      <span className={options.labelClassName}>{item.label}</span>
      <ExternalLink size={16} className="tw-text-neutral-950 tw-ml-auto" />
    </a>
  );

  const profileButtonItems = (): MenuItem[] => {
    const logoutButton: MenuItem = {
      label: 'Logout',
      className: 'tw-flex tw-flex-row tw-gap-x-2 tw-font-atkinson tw-text-neutral-950 tw-w-full ',
      icon: <LogOut size={20} className="tw-text-neutral-950" />,
      command: () => {
        commitLoginMutation({
          variables: {
            input: {
              userAction: {
                userId: `UserNode:${userRedux?.id}`,
                actionTypeId: 'ActionTypeNode:2',
                timeStamp: new Date().toISOString(),
                description: 'logout',
              },
            },
          },
        });
        dispatch(resetUser());
        posthog.reset();
        logout({ logoutParams: { returnTo: window.location.origin } });
      },
    };

    const knowledgeButton: MenuItem = {
      label: 'Knowledge Base',
      className: 'tw-flex tw-flex-row tw-gap-x-2 tw-font-atkinson tw-text-neutral-950 tw-w-full tw-pb-2',
      template: externalLinkTemplate,
      command: () => {
        openExternalUrl('https://newclassrooms-12mm.help.userguiding.com/en');
      },
    };

    const guidedButton: MenuItem = {
      label: 'Guided Walkthroughs',
      className: 'tw-flex tw-flex-row tw-gap-x-2 tw-font-atkinson tw-text-neutral-950 tw-w-full tw-pb-2',
      items: [
        {
          label: 'Prepare for the Diagnostic',
          command: () => {
            navigate('/prep-diagnostics');
          },
        },
        {
          label: 'Student Experience Overview',
          command: () => {
            navigate('/how-students-learn');
          },
        },
        {
          label: 'Roadmaps Course',
          template: externalLinkTemplate,
          command: () => {
            openExternalUrl('https://teachtoone.org/roadmapscourse');
          },
        },
      ],
    };

    const interactiveButton: MenuItem = {
      label: 'Interactive Demos',
      className: 'tw-flex tw-flex-row tw-gap-x-2 tw-font-atkinson tw-text-neutral-950 tw-w-full tw-pb-2',
      items: [
        {
          label: 'Grades 4th–6th Skill',
          command: () => {
            navigate('/guided-tour/perimeter-and-area-of-rectangles', { state: { from: currentPath() } });
          },
        },
        {
          label: 'Grades 7th–Algebra Skill',
          command: () => {
            navigate('/guided-tour/proportion-graphs', { state: { from: currentPath() } });
          },
        },
        {
          label: 'Diagnostic',
          command: () => {
            navigate('/diagnostic-preview');
          },
        },
      ],
    };

    const timeZonesButtons: MenuItem[] = timeZonesList.map(timeZoneName => ({
      label: timeZoneName,
      className: timeZoneName === userRedux?.activeTimeZone ? 'p-highlight' : '',
      command: () => {
        dispatch(setUser({ ...userRedux, activeTimeZone: timeZoneName } as User));
      },
    }));

    if (!isTeacherOrSiteAdminOrNetworkAdmin) {
      return [logoutButton];
    }

    const accessibilityButton: MenuItem = {
      label: 'Accessibility tools',
      className: 'tw-flex tw-flex-row tw-gap-x-2 tw-font-atkinson tw-text-neutral-950 tw-w-full tw-pb-2',
      template: externalLinkTemplate,
      disabled: loadingToolbar,
      command: () => {
        loadToolbar();
      },
    };

    const termsButton: MenuItem = {
      label: 'Terms & Rules',
      className: 'tw-flex tw-flex-row tw-gap-x-2 tw-font-atkinson tw-text-neutral-950 tw-w-full tw-pb-2',
      template: externalLinkTemplate,
      command: () => {
        openExternalUrl('https://teachtoone.org/terms-of-use/');
      },
    };

    const timeZoneItems: MenuItem[] =
      timeZonesList.length < 2 ? [] : [{ label: 'Set Time Zone', items: timeZonesButtons }];

    if (!sideNavActive) {
      return [interactiveButton, guidedButton, knowledgeButton, { separator: true }, ...timeZoneItems, logoutButton];
    }

    return [
      interactiveButton,
      guidedButton,
      knowledgeButton,
      {
        separator: true,
      },
      accessibilityButton,
      termsButton,
      ...timeZoneItems,
      logoutButton,
    ];
  };

  const profileA11yLabel = `${userRedux?.name ?? 'User'} user profile menu`;

  return (
    <div data-testid="profile_menu">
      <TieredMenu
        model={profileButtonItems()}
        popup
        ref={profileMenu}
        onShow={() => setToggleButton(true)}
        onHide={() => setToggleButton(false)}
        id="profile_menu"
        pt={{
          menuitem: {
            'aria-level': undefined,
          },
        }}
        className="nc_profile-tired-menu"
      />
      {/* PROFILE MENU BUTTON FOR DESKTOP SCREENS */}
      <Button
        data-hj-suppress
        rounded
        outlined
        onClick={event => {
          profileMenu?.current?.toggle(event);
        }}
        aria-label={profileA11yLabel}
        aria-haspopup="menu"
        aria-controls="profile_menu"
        aria-expanded={toggleButton}
        className={
          `tw-gap-x-2 nc_profile-button ${toggleButton ? 'active' : ''} ` +
          (isTeacherOrSiteAdminOrNetworkAdmin ? '' : ' ph-no-capture')
        }
        pt={{}}
      >
        <Avatar image={profileAvatarImage} shape="circle" />
        <span className="nc_profile-name tw-hidden md:tw-flex">{userRedux?.name}</span>
        {toggleButton ? (
          <ChevronUp size={24} className="tw-text-neutral-950 tw-hidden md:tw-flex " />
        ) : (
          <ChevronDown size={24} className="tw-text-neutral-950 tw-hidden md:tw-flex" />
        )}
      </Button>
      {/* PROFILE MENU BUTTON FOR SMALL/MOBILE SCREENS */}
      {/* <Button
        data-hj-suppress
        icon={<Avatar image={profileAvatarImage} shape="circle" />}
        rounded
        outlined
        onClick={event => profileMenu?.current?.toggle(event)}
        aria-label={profileA11yLabel}
        aria-haspopup="menu"
        aria-controls="profile_menu"
        className={
          'md:hidden tw-flex nc_profile-button' +
          (isTeacherOrSiteAdminOrNetworkAdmin ? '' : ' ph-no-capture')
        }
      >
        <Avatar image={profileAvatarImage} shape="circle" />
        <span className="nc_profile-name md:hidden tw-flex">{userRedux?.name}</span>
        {profileMenu.current?.props ? (
          <ChevronUp size={24} className="tw-text-neutral-950 md:hidden tw-flex" />
        ) : (
          <ChevronDown size={24} className="tw-text-neutral-950 md:hidden tw-flex" />
        )}
        {/* <span className="p-sr-only">{profileA11yLabel}</span> */}
      {/* </Button> */}
    </div>
  );
};

export default HeaderProfileMenu;
